import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	KeyboardEvent
} from "react";
import { CollapsibleMenu } from "../CollapsibleMenu/CollapsibleMenu";
import { ActionBarButton } from "./ActionBarButton/ActionBarButton";
import { ActionBarSpaceBetween } from "./ActionBarSpaceBetween/ActionBarSpaceBetween";
import { ActionBarMenuButton } from "./ActionBarMenuButton/ActionBarMenuButton";
import styles from "./ActionBar.module.less";
import { setTrackingId } from "client/Misc/utils";
import classNames from "classnames";
import { IconButton } from "../IconButton/IconButton";
import { ActionBarDivider } from "./ActionBarDivider";

export type ActionBarHorizontalAlignment =
	| "default"
	| "page-content"
	| "no";

export type ActionBarProps = {
	horizontalAlignment?: ActionBarHorizontalAlignment;
	hasBorderBottom?: boolean;
	trackingId: string;
};

export const ActionBar = (props: PropsWithChildren<ActionBarProps>): ReactElement => {
	const {
		horizontalAlignment = "default",
		hasBorderBottom = false,
		trackingId,
		children
	} = props;

	const renderMoreButton = useCallback(
		(
			isActive: boolean,
			tabIndex: number,
			onIsActiveChange: (value: boolean) => void,
			onKeyDown: (event: KeyboardEvent<HTMLElement>) => void,
			ref: (element: HTMLElement | null) => void
		): ReactElement => {
			return (
				<IconButton
					onClick={() => onIsActiveChange(!isActive)}
					trackingId="more-actions"
					kind="transparent"
					tabIndex={tabIndex}
					icon="ellipsis"
					size="large"
					onKeyDown={onKeyDown}
					ref={ref}
				/>
			);
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				alignmentToStyleMap[horizontalAlignment],
				{ [styles.root_hasBorderBottom]: hasBorderBottom }
			)}
		>
			<CollapsibleMenu
				moreButtonFactory={renderMoreButton}
				horizontalAlignment="left"
				shouldPushMoreButtonRight
				height="fill"
			>
				{children}
			</CollapsibleMenu>
		</div>
	);
};
ActionBar.displayName = "ActionBar";

ActionBar.Button = ActionBarButton;
ActionBar.MenuButton = ActionBarMenuButton;
ActionBar.SpaceBetween = ActionBarSpaceBetween;
ActionBar.Divider = ActionBarDivider;

const alignmentToStyleMap: Record<ActionBarHorizontalAlignment, string | undefined> = {
	"default": undefined,
	"no": styles.root_alignmentNo,
	"page-content": styles.root_alignmentPageContent
};
